import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";

const PolitiqueConfidentialitePage = () => {
  const { t } = useTranslation();

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="politique-confidentialite-page">
      <SEO title="Politique de Confidentialité" path="/politique-de-confidentialite" description="Politique de confidentialité du site ALTURA Conciergerie — AD MOBILITÉ SASU. Protection des données personnelles, RGPD, cookies." noindex />

      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] mb-12">
              Politique de confidentialité
            </h1>

            <div className="space-y-10 text-[#1A1A1A]/80 text-sm font-light leading-relaxed">

              <div>
                <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">1. Responsable du traitement</h2>
                <p>Le responsable du traitement des données personnelles collectées sur le site alturaconciergerie.com est :</p>
                <ul className="mt-3 space-y-1">
                  <li><strong>AD MOBILITÉ</strong> — SASU au capital variable</li>
                  <li>SIRET : 981 338 403 00018</li>
                  <li>19 rue des Sablières, 78410 Aubergenville, France</li>
                  <li>Email : <a href="mailto:contact@alturaconciergerie.com" className="underline hover:text-[#1A1A1A]">contact@alturaconciergerie.com</a></li>
                  <li>Représentant légal : Adrien Garcia, Président</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">2. Données collectées</h2>
                <p>Dans le cadre de nos services, nous collectons les données personnelles suivantes :</p>
                <ul className="mt-3 space-y-2">
                  <li><strong>Lors d'une réservation :</strong> nom, prénom, adresse email, numéro de téléphone, adresses de prise en charge et de destination, numéro de vol ou de train (facultatif), observations particulières.</li>
                  <li><strong>Lors d'une demande de contact :</strong> nom, adresse email, numéro de téléphone, message.</li>
                  <li><strong>Lors d'un paiement en ligne :</strong> les données bancaires sont traitées directement par notre prestataire de paiement <strong>Stripe</strong> (stripe.com). AD MOBILITÉ n'a jamais accès à vos données bancaires complètes.</li>
                  <li><strong>Données de navigation :</strong> adresse IP, type de navigateur, pages consultées, durée de visite (via cookies techniques).</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">3. Finalité du traitement</h2>
                <p>Les données collectées sont utilisées pour :</p>
                <ul className="mt-3 space-y-1 list-disc list-inside">
                  <li>Traiter et confirmer vos réservations de transport</li>
                  <li>Vous envoyer des confirmations par email (récapitulatif, PDF de réservation)</li>
                  <li>Répondre à vos demandes de contact et de devis</li>
                  <li>Améliorer nos services et l'expérience utilisateur du site</li>
                  <li>Respecter nos obligations légales et comptables</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">4. Base légale du traitement</h2>
                <p>Le traitement de vos données repose sur :</p>
                <ul className="mt-3 space-y-1 list-disc list-inside">
                  <li><strong>L'exécution d'un contrat</strong> : lorsque vous effectuez une réservation</li>
                  <li><strong>Le consentement</strong> : lorsque vous remplissez un formulaire de contact</li>
                  <li><strong>L'intérêt légitime</strong> : pour améliorer nos services et la sécurité du site</li>
                  <li><strong>L'obligation légale</strong> : pour nos obligations comptables et fiscales</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">5. Durée de conservation</h2>
                <ul className="space-y-2">
                  <li><strong>Données de réservation :</strong> conservées pendant 3 ans à compter de la dernière prestation, puis archivées 5 ans pour les obligations comptables.</li>
                  <li><strong>Données de contact :</strong> conservées pendant 1 an à compter du dernier échange.</li>
                  <li><strong>Données de navigation :</strong> conservées 13 mois maximum conformément aux recommandations de la CNIL.</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">6. Destinataires des données</h2>
                <p>Vos données personnelles peuvent être transmises aux destinataires suivants :</p>
                <ul className="mt-3 space-y-1 list-disc list-inside">
                  <li><strong>Stripe Inc.</strong> — traitement des paiements en ligne</li>
                  <li><strong>Hostinger</strong> — envoi des emails de confirmation</li>
                  <li><strong>Google</strong> — services de cartographie (Google Maps API) pour le calcul des trajets</li>
                </ul>
                <p className="mt-3">Aucune donnée n'est vendue, louée ou cédée à des tiers à des fins commerciales.</p>
              </div>

              <div>
                <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">7. Transfert hors UE</h2>
                <p>
                  Certains de nos prestataires (Stripe, Google) peuvent traiter des données en dehors de l'Union Européenne. Ces transferts sont encadrés par des garanties appropriées (clauses contractuelles types de la Commission Européenne, certifications Privacy Shield ou équivalent).
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">8. Cookies</h2>
                <p>Le site utilise des cookies strictement nécessaires au fonctionnement du service :</p>
                <ul className="mt-3 space-y-1 list-disc list-inside">
                  <li><strong>Cookies techniques :</strong> gestion de la langue, préférences d'affichage</li>
                  <li><strong>Cookies de session :</strong> maintien de votre navigation</li>
                </ul>
                <p className="mt-3">Aucun cookie publicitaire ou de tracking tiers n'est utilisé sur ce site.</p>
              </div>

              <div>
                <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">9. Vos droits (RGPD)</h2>
                <p>Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés, vous disposez des droits suivants :</p>
                <ul className="mt-3 space-y-1 list-disc list-inside">
                  <li><strong>Droit d'accès :</strong> obtenir une copie de vos données personnelles</li>
                  <li><strong>Droit de rectification :</strong> corriger des données inexactes ou incomplètes</li>
                  <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
                  <li><strong>Droit à la limitation :</strong> restreindre le traitement de vos données</li>
                  <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
                  <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
                </ul>
                <p className="mt-3">
                  Pour exercer ces droits, contactez-nous à : <a href="mailto:contact@alturaconciergerie.com" className="underline hover:text-[#1A1A1A]">contact@alturaconciergerie.com</a>
                </p>
                <p className="mt-3">
                  Vous disposez également du droit d'introduire une réclamation auprès de la <strong>CNIL</strong> (Commission Nationale de l'Informatique et des Libertés) : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#1A1A1A]">www.cnil.fr</a>
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">10. Sécurité</h2>
                <p>
                  AD MOBILITÉ met en œuvre les mesures techniques et organisationnelles appropriées pour protéger vos données personnelles contre l'accès non autorisé, la perte, la destruction ou l'altération. Le site utilise le protocole HTTPS pour sécuriser les échanges de données.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">11. Modification de la politique</h2>
                <p>
                  AD MOBILITÉ se réserve le droit de modifier la présente politique de confidentialité à tout moment. Les modifications prennent effet dès leur publication sur le site. Nous vous invitons à consulter cette page régulièrement.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">12. Contact</h2>
                <p>
                  Pour toute question concernant cette politique de confidentialité ou le traitement de vos données personnelles :
                </p>
                <ul className="mt-3 space-y-1">
                  <li><strong>Email :</strong> <a href="mailto:contact@alturaconciergerie.com" className="underline hover:text-[#1A1A1A]">contact@alturaconciergerie.com</a></li>
                  <li><strong>Courrier :</strong> AD MOBILITÉ — 19 rue des Sablières, 78410 Aubergenville, France</li>
                </ul>
              </div>

            </div>

            <p className="mt-12 text-[#1A1A1A]/40 text-xs">Dernière mise à jour : février 2026</p>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default PolitiqueConfidentialitePage;
